import { IoBackspaceOutline } from 'react-icons/io5';

import type { ICalculatorButton, ICalculatorKeypadLayout, IUnitItem, TConverterTab } from '@src/types';

import styles from '../calculator.module.scss';

export const OPERATORS = ['+', '-', '×', '÷'];
export const MAX_NUMBER_LENGTH = 15;

const createNumberButton = (num: string): ICalculatorButton => ({
  label: num,
  value: num,
  classes: '',
  onClick: 'handleNumber',
});

const NUMBER_7 = createNumberButton('7');
const NUMBER_8 = createNumberButton('8');
const NUMBER_9 = createNumberButton('9');
const NUMBER_4 = createNumberButton('4');
const NUMBER_5 = createNumberButton('5');
const NUMBER_6 = createNumberButton('6');
const NUMBER_1 = createNumberButton('1');
const NUMBER_2 = createNumberButton('2');
const NUMBER_3 = createNumberButton('3');
const NUMBER_0 = createNumberButton('0');
const NUMBER_0_CUSTOM: ICalculatorButton = { label: '0', classes: 'col-2', onClick: 'handleNumber' };
const DECIMAL: ICalculatorButton = { label: '.', classes: '', onClick: 'handleDecimal' };

export const BASIC_KEYPAD: ICalculatorKeypadLayout = {
  cols: 4,
  buttons: [
    {
      label: 'AC',
      classes: 'yellow',
      onClick: 'handleClear',
    },
    {
      label: '%',
      onClick: 'handlePercent',
    },
    {
      label: <IoBackspaceOutline size={25} className="back-btn" />,
      classes: '',
      onClick: 'handleBackspace',
    },
    {
      label: '÷',
      onClick: 'handleOperator',
    },
    NUMBER_7,
    NUMBER_8,
    NUMBER_9,
    {
      label: '×',
      onClick: 'handleOperator',
    },
    NUMBER_4,
    NUMBER_5,
    NUMBER_6,
    {
      label: '+',
      classes: '',
      onClick: 'handleOperator',
    },
    NUMBER_1,
    NUMBER_2,
    NUMBER_3,
    {
      label: '-',
      onClick: 'handleOperator',
    },
    NUMBER_0_CUSTOM,
    DECIMAL,
    {
      label: '=',
      onClick: 'handleEquals',
    },
  ],
};

export const CONVERTER_KEYPAD: ICalculatorKeypadLayout = {
  cols: 4,
  buttons: [
    NUMBER_7,
    NUMBER_8,
    NUMBER_9,
    {
      label: (
        <div className={styles.acButtonContainer}>
          <span className={styles.acButtonText}>A</span>
          <span className={styles.acButtonText}>C</span>
        </div>
      ),
      classes: 'yellow row-2',
      onClick: 'handleClear',
    },
    NUMBER_4,
    NUMBER_5,
    NUMBER_6,
    NUMBER_1,
    NUMBER_2,
    NUMBER_3,
    {
      label: <IoBackspaceOutline size={25} className="back-btn" />,
      classes: 'row-2',
      onClick: 'handleBackspace',
    },
    NUMBER_0_CUSTOM,
    DECIMAL,
  ],
};

export const SCIENTIFIC_KEYPAD: ICalculatorKeypadLayout = {
  cols: 5,
  buttons: [
    {
      label: 'AC',
      classes: 'yellow',
      onClick: 'handleClear',
    },
    {
      label: '%',
      onClick: 'handlePercent',
    },
    {
      label: '√',
      value: '√',
      onClick: 'handleOperator',
    },
    {
      label: 'π',
      value: 'π',
      onClick: 'handleOperator',
    },
    {
      label: <IoBackspaceOutline size={25} className="back-btn" />,
      onClick: 'handleBackspace',
    },
    {
      label: 'x²',
      value: 'x²',
      onClick: 'handleOperator',
    },
    {
      label: 'x³',
      value: 'x³',
      onClick: 'handleOperator',
    },
    {
      label: 'sin',
      value: 'sin',
      onClick: 'handleOperator',
    },
    {
      label: 'cos',
      value: 'cos',
      onClick: 'handleOperator',
    },
    {
      label: 'tan',
      value: 'tan',
      onClick: 'handleOperator',
    },
    {
      label: 'eˣ',
      value: 'eˣ',
      onClick: 'handleOperator',
    },
    {
      label: '1/x',
      value: '1/x',
      onClick: 'handleOperator',
    },
    {
      label: 'csc',
      value: 'csc',
      onClick: 'handleOperator',
    },
    {
      label: 'sec',
      value: 'sec',
      onClick: 'handleOperator',
    },
    {
      label: 'cot',
      value: 'cot',
      onClick: 'handleOperator',
    },
    NUMBER_7,
    NUMBER_8,
    NUMBER_9,
    {
      label: '÷',
      onClick: 'handleOperator',
    },
    {
      label: '×',
      onClick: 'handleOperator',
    },
    NUMBER_4,
    NUMBER_5,
    NUMBER_6,
    {
      label: '±',
      value: '±',
      onClick: 'handleOperator',
    },
    {
      label: '-',
      onClick: 'handleOperator',
    },
    NUMBER_1,
    NUMBER_2,
    NUMBER_3,
    {
      label: 'n!',
      value: 'n!',
      onClick: 'handleOperator',
    },
    {
      label: '+',
      onClick: 'handleOperator',
    },
    NUMBER_0,
    DECIMAL,
    {
      label: '(',
      onClick: 'handleNumber',
    },
    {
      label: ')',
      onClick: 'handleNumber',
    },
    {
      label: '=',
      onClick: 'handleEquals',
    },
  ],
};

export const converterTabs: TConverterTab[] = [
  'angle',
  'area',
  'currency',
  'data',
  'length',
  'speed',
  'temperature',
  'time',
  'volume',
  'weight',
];

export const converterUnitsMap: Record<TConverterTab, IUnitItem[]> = {
  angle: [
    { code: 'deg', name: 'Degrees' }, // mathjs: "deg"
    { code: 'rad', name: 'Radians' }, // mathjs: "rad"
    { code: 'grad', name: 'Gradians' }, // mathjs: "grad"
  ],
  area: [
    { code: 'm²', name: 'Square meter', mathjsName: 'm^2' },
    { code: 'km²', name: 'Square kilometer', mathjsName: 'km^2' },
    { code: 'ft²', name: 'Square foot', mathjsName: 'ft^2' },
    { code: 'acre', name: 'Acre' }, // mathjs: "acre"
    { code: 'ha', name: 'Hectare' }, // mathjs: "ha"
  ],
  currency: [
    { code: 'USD', name: 'US Dollar' },
    { code: 'EUR', name: 'Euro' },
    { code: 'GBP', name: 'British Pound' },
    { code: 'NIS', name: 'Israeli Shekel' },
    { code: 'JPY', name: 'Japanese Yen' },
  ],
  data: [
    { code: 'B', name: 'Byte', mathjsName: 'byte' }, // mathjs uses "byte"
    { code: 'KB', name: 'Kilobyte', mathjsName: 'kB' },
    { code: 'MB', name: 'Megabyte', mathjsName: 'MB' },
    { code: 'GB', name: 'Gigabyte', mathjsName: 'GB' },
    { code: 'TB', name: 'Terabyte', mathjsName: 'TB' },
  ],
  length: [
    { code: 'mm', name: 'Millimeter' }, // mathjs: "mm"
    { code: 'cm', name: 'Centimeter' },
    { code: 'm', name: 'Meter' },
    { code: 'km', name: 'Kilometer' },
    { code: 'inch', name: 'Inch' },
    { code: 'ft', name: 'Foot' },
    { code: 'mi', name: 'Mile' },
  ],
  speed: [
    { code: 'm/s', name: 'Meters per second', mathjsName: 'm / s' },
    { code: 'km/h', name: 'Kilometers per hour', mathjsName: 'km / h' },
    { code: 'mph', name: 'Miles per hour', mathjsName: 'mi / h' },
    { code: 'ft/s', name: 'Feet per second', mathjsName: 'ft / s' },
  ],
  temperature: [
    { code: '°C', name: 'Celsius', mathjsName: 'degC' },
    { code: '°F', name: 'Fahrenheit', mathjsName: 'degF' },
    { code: 'K', name: 'Kelvin', mathjsName: 'K' },
  ],
  time: [
    { code: 'ms', name: 'Millisecond' }, // "ms"
    { code: 's', name: 'Second' }, // "s"
    { code: 'min', name: 'Minute' }, // "min"
    { code: 'h', name: 'Hour', mathjsName: 'hour' }, // avoid "h" → henry
    { code: 'day', name: 'Day' }, // "day"
  ],
  volume: [
    { code: 'ml', name: 'Milliliter' }, // "ml"
    { code: 'L', name: 'Liter' }, // "L"
    { code: 'm³', name: 'Cubic meter', mathjsName: 'm^3' },
    { code: 'ft³', name: 'Cubic foot', mathjsName: 'ft^3' },
    { code: 'gal', name: 'Gallon (US)' }, // "gal"
  ],
  weight: [
    { code: 'g', name: 'Gram' }, // "g"
    { code: 'kg', name: 'Kilogram' }, // "kg"
    { code: 'lb', name: 'Pound' }, // "lb"
    { code: 'oz', name: 'Ounce' }, // "oz"
    { code: 'ton', name: 'Metric ton' }, // "ton"
  ],
};
